/* Estate Quality Scores — the monitoring and diagnosis workspace.
 *
 * Composition only. Every figure comes from a hook, every rule from `model/`, and every pixel from
 * a component below this file; what lives here is the wiring and the page's state.
 *
 * The order of the page is the order of the work: **monitor → detect → explain → investigate →
 * act.** The estate strip answers "how are we", the regression feed answers "did anything break",
 * the trend answers "which way are we going", the ledger is where the work is, and the drawer is
 * every step after a reader has picked a row. A screen that opened with the ledger would make a
 * reader derive the first three questions from a table.
 *
 * **Three batch reads serve the whole page, never one per row**: the catalogue walk for names, one
 * `/scores/latest` for every score, and one `/catalog/datasets/facts` so an unscored row can tell
 * "never profiled" from "profiled, no rules yet". Contracts are a fourth, and a fourth that pays
 * for itself — it carries every quality target and every named owner on the page.
 *
 * The two *fan-outs* are movement and per-dimension figures, and both are governed here rather than
 * inside their hooks: this is the layer that knows how big the estate is, and the policy for
 * "automatic while it is cheap, asked for beyond that" belongs in one place.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useQueryClient } from '@tanstack/react-query';

import {
  useDatasetFacts,
  useDatasetNames,
  useLatestScores,
} from '@/features/catalog-browser';

import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { UnavailableState } from '@/shared/ui/States';

import { AUTO_MOVEMENT_LIMIT, useMovements } from '../api/movements';
import { useEstate } from '../api/scoring';
import { DIMENSION_READ_LIMIT, useDatasetScores } from '../api/scoresBatch';
import { useQualityTargets } from '../api/targets';
import { MOVEMENT_WINDOW_DAYS, PERIOD_OPTIONS } from '../model/movement';
import { DEFAULT_THRESHOLDS, detectRegressions } from '../model/regressions';
import {
  BUILT_IN_VIEWS,
  DEFAULT_QUERY,
  applyQuery,
  buildRows,
  countByStatus,
  queryIsFiltered,
  schemasOf,
} from '../model/rows';
import type { SavedView, SortKey, TableQuery } from '../model/rows';
import type { DrawerTab, OptionalColumn } from '../model/types';

import { DatasetDrawer } from './DatasetDrawer';
import { DatasetTable } from './DatasetTable';
import { DatasetToolbar } from './DatasetToolbar';
import { QualitySummary } from './QualitySummary';
import { QualityTrend } from './QualityTrend';
import { RegressionFeed } from './RegressionFeed';

/** Which of the optional columns a reader sees before they have chosen. Coverage and target are on
 * because a score without either is a number nobody can act on; dimensions are off because they
 * cost a request per dataset, and a default that fans out is a default nobody opted into. */
const DEFAULT_COLUMNS: readonly OptionalColumn[] = [
  'coverage',
  'target',
  'evaluated',
  'owner',
];

/** A reader's own view: a name, and the query that was in force when they saved it. Stored here as
 * component state, which is where §18 puts it for this phase — the shape is what a
 * `GET /v1/scoring/views` would return, so connecting one replaces this array and nothing else. */
interface CustomView {
  readonly id: string;
  readonly label: string;
  readonly query: TableQuery;
}

export function ScoresScreen(): ReactElement {
  const { t } = useTranslation();
  const client = useQueryClient();

  const [query, setQuery] = useState<TableQuery>(DEFAULT_QUERY);
  const [columns, setColumns] = useState<ReadonlySet<OptionalColumn>>(
    () => new Set(DEFAULT_COLUMNS),
  );
  const [custom, setCustom] = useState<readonly CustomView[]>([]);
  /* **Selection and the drawer are two states, not one.** A row stays chosen after the drawer is
     closed, which is what lets the trend above keep showing that dataset while a reader scrolls the
     ledger — closing a panel is not the same gesture as saying "never mind, show me the estate",
     and the chart's own "Back to the estate" is where that second gesture lives. */
  const [selected, setSelected] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('decomposition');
  const [days, setDays] = useState(MOVEMENT_WINDOW_DAYS);
  const [movementOverride, setMovementOverride] = useState<boolean | null>(null);
  const [dimensionsOn, setDimensionsOn] = useState(false);
  const searchBox = useRef<HTMLDivElement | null>(null);

  const names = useDatasetNames();
  const ids = useMemo(() => [...(names.data?.byId.keys() ?? [])], [names.data]);
  const scores = useLatestScores(ids);
  const facts = useDatasetFacts(ids);
  const targets = useQualityTargets();
  const estate = useEstate();

  /* Automatic while it is cheap, asked for beyond that. The bound is the *estate*, not the visible
     page: filtering to six rows does not make a five-hundred-dataset fan-out affordable, because
     clearing the filter is one click away. */
  const movementAffordable = ids.length > 0 && ids.length <= AUTO_MOVEMENT_LIMIT;
  const movementOn = movementOverride ?? movementAffordable;
  const dimensionsAllowed = ids.length > 0 && ids.length <= DIMENSION_READ_LIMIT;

  const movements = useMovements(ids, days, movementOn);
  const dimensionScores = useDatasetScores(ids, dimensionsOn && dimensionsAllowed);

  const rows = useMemo(
    () =>
      buildRows({
        names: names.data?.byId ?? new Map<string, string>(),
        scores: scores.data,
        facts: facts.data,
        targets: targets.byDataset,
        movements: movements.byDataset,
        now: Date.now(),
      }),
    [names.data, scores.data, facts.data, targets.byDataset, movements.byDataset],
  );

  const views = useMemo<readonly SavedView[]>(() => {
    const saved = custom.map((entry) => {
      const base = BUILT_IN_VIEWS.find((view) => view.id === entry.query.view);
      return {
        id: entry.id,
        label: entry.label,
        builtIn: false,
        /* A saved view's *population* is its base view's; the search term and schema it was saved
           with are applied by `applyQuery` from the restored query. Folding them into the predicate
           too would count them twice. */
        predicate: base?.predicate ?? ((): boolean => true),
      } satisfies SavedView;
    });
    return [...BUILT_IN_VIEWS, ...saved];
  }, [custom]);

  const visible = useMemo(() => applyQuery(rows, query, views), [rows, query, views]);
  const counts = useMemo(() => countByStatus(rows), [rows]);
  const schemas = useMemo(() => schemasOf(rows), [rows]);
  const viewCounts = useMemo(
    () => new Map(views.map((view) => [view.id, rows.filter(view.predicate).length])),
    [views, rows],
  );

  const regressions = useMemo(() => detectRegressions(rows, DEFAULT_THRESHOLDS), [rows]);

  /* Runs inside the window, summed from the movement reads. `null` when they are off, because a
     count nobody has read is not a zero. */
  const runsInWindow = useMemo(() => {
    if (!movementOn || movements.byDataset.size === 0) return null;
    let total = 0;
    for (const movement of movements.byDataset.values()) {
      total += movement.points.filter((point) => point !== null).length;
    }
    return total;
  }, [movementOn, movements.byDataset]);

  const openDrawer = useCallback((datasetId: string, tab: DrawerTab) => {
    setSelected(datasetId);
    setDrawerTab(tab);
    setDrawerOpen(true);
  }, []);

  const onSort = useCallback((key: SortKey) => {
    setQuery((current) =>
      current.sort === key
        ? { ...current, direction: current.direction === 1 ? -1 : 1 }
        : { ...current, sort: key, direction: 1 },
    );
  }, []);

  const onToggleColumn = useCallback((column: OptionalColumn) => {
    setColumns((current) => {
      const next = new Set(current);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return next;
    });
  }, []);

  /* `/` focuses the search box, which is the one keyboard affordance an operator on a listing
     screen expects. Guarded on the event target so typing a slash inside a field does not steal
     focus from the field the reader is already in. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== '/') return;
      const active = document.activeElement;
      if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement)
        return;
      if (active instanceof HTMLSelectElement) return;
      const input = searchBox.current?.querySelector('input');
      if (input === null || input === undefined) return;
      event.preventDefault();
      input.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  /* Looked up in `rows` rather than in `visible`, so a row that a filter has since hidden keeps
     its drawer open instead of vanishing mid-diagnosis. */
  const selectedRow = rows.find((row) => row.id === selected) ?? null;

  const pageHeader = (
    <PageHeader
      title={t('scoring.title')}
      lede={t('scoring.lede')}
      actions={
        <button
          type="button"
          className="lq-btn lq-btn--secondary lq-btn--compact"
          onClick={() => {
            /* The whole namespace: the estate figure, every listing, every trend and every stored
               score are all derived from the rows a scoring run writes, so a refresh that missed
               one would leave two figures on this page disagreeing. */
            void client.invalidateQueries({ queryKey: ['scoring'] });
            void client.invalidateQueries({ queryKey: ['contracts'] });
          }}
        >
          {t('scoring.refresh')}
        </button>
      }
      meta={
        !targets.available ? (
          <span className="lq-muted">{t('scoring.targets.unreadable')}</span>
        ) : targets.complete ? undefined : (
          <span className="lq-muted">{t('scoring.targets.partial')}</span>
        )
      }
    />
  );

  if (names.isPending) {
    return (
      <div className="lq-scores-page lq-stack">
        {pageHeader}
        <SkeletonRows rows={6} columns={5} />
      </div>
    );
  }

  if (names.isError) {
    return (
      <div className="lq-scores-page lq-stack">
        {pageHeader}
        <UnavailableState
          title={t('scoring.listing.unreadable')}
          guidance={names.error.message}
        />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="lq-scores-page lq-stack">
        {pageHeader}
        <EmptyState
          title={t('scoring.listing.noDatasets')}
          guidance={t('scoring.listing.noDatasetsGuidance')}
        />
      </div>
    );
  }

  return (
    <div className="lq-scores-page lq-stack">
      {pageHeader}

      <QualitySummary
        estate={estate.data}
        loading={estate.isPending}
        error={estate.isError ? estate.error : null}
        counts={counts}
        total={rows.length}
        runsInWindow={runsInWindow}
        activeView={query.view}
        onSelectView={(view) => {
          setQuery((current) => ({ ...current, view }));
        }}
      />

      <RegressionFeed
        regressions={regressions}
        thresholds={DEFAULT_THRESHOLDS}
        movementRead={movementOn && !movements.loading}
        onOpen={openDrawer}
      />

      <QualityTrend
        days={days}
        onDaysChange={setDays}
        periods={PERIOD_OPTIONS}
        datasetId={selectedRow?.id ?? null}
        datasetName={selectedRow?.name ?? null}
        onClearDataset={() => {
          setSelected(null);
          setDrawerOpen(false);
        }}
      />

      <div ref={searchBox}>
        <DatasetToolbar
          views={views}
          counts={viewCounts}
          query={query}
          onQueryChange={(next) => {
            /* Restoring a saved view restores the search and the schema it was saved with, not just
               its base population — a view that dropped half of what a reader saved is a view they
               stop trusting. */
            const saved = custom.find((entry) => entry.id === next.view);
            setQuery(
              saved === undefined
                ? next
                : {
                    ...saved.query,
                    view: saved.id,
                    sort: next.sort,
                    direction: next.direction,
                  },
            );
          }}
          schemas={schemas}
          columns={columns}
          onToggleColumn={onToggleColumn}
          filtered={queryIsFiltered(query)}
          onReset={() => {
            setQuery({ ...DEFAULT_QUERY, sort: query.sort, direction: query.direction });
          }}
          shown={visible.length}
          total={rows.length}
          movementOn={movementOn}
          movementLoading={movements.loading}
          onToggleMovement={() => {
            setMovementOverride(!movementOn);
          }}
          dimensionsOn={dimensionsOn}
          dimensionsLoading={dimensionScores.loading}
          dimensionsAllowed={dimensionsAllowed}
          dimensionLimit={DIMENSION_READ_LIMIT}
          onToggleDimensions={() => {
            setDimensionsOn((on) => {
              const next = !on;
              /* Turning the figures on is also what makes the column worth having on screen, so the
                 two move together rather than leaving a reader to find the column picker. */
              setColumns((current) => {
                const updated = new Set(current);
                if (next) updated.add('dimensions');
                else updated.delete('dimensions');
                return updated;
              });
              return next;
            });
          }}
          onSaveView={(label) => {
            const id = `saved-${String(Date.now())}`;
            setCustom((current) => [...current, { id, label, query }]);
            setQuery((current) => ({ ...current, view: id }));
          }}
          onDeleteView={(id) => {
            setCustom((current) => current.filter((entry) => entry.id !== id));
            setQuery((current) =>
              current.view === id ? { ...current, view: 'all' } : current,
            );
          }}
        />
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title={t('scoring.listing.noMatch')}
          guidance={t('scoring.listing.noMatchGuidance')}
        />
      ) : (
        <DatasetTable
          rows={visible}
          columns={columns}
          sort={query.sort}
          direction={query.direction}
          onSort={onSort}
          selected={selected}
          onOpen={openDrawer}
          movementOn={movementOn}
          movementLoading={movements.loading}
          scores={dimensionScores.byKey}
          dimensionsLoading={dimensionScores.loading}
          windowDays={days}
        />
      )}

      {scores.isError && (
        <p className="lq-attention__blind">{t('scoring.listing.scoresUnreadable')}</p>
      )}

      {drawerOpen && selectedRow !== null && (
        <DatasetDrawer
          row={selectedRow}
          tab={drawerTab}
          onTab={setDrawerTab}
          days={days}
          onClose={() => {
            setDrawerOpen(false);
          }}
        />
      )}
    </div>
  );
}
